import type { KeyboardEvent, MouseEvent } from 'react';

/** The props a whole-row `role="button"` needs — spread onto the row element
 *  (`<li>`, `<div>`), which supplies its own className and data attributes. */
export interface IRowActivationProps {
  role: 'button';
  tabIndex: number;
  'aria-label': string;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

/**
 * A row's mouse click that opens its record — unless it landed on a nested
 * `<button>`, whose click is that button's own. For a row that names a real
 * button as its keyboard and screen-reader action and keeps the whole-row
 * click only as a pointer convenience (the Inventory Levels rows).
 */
export function rowClickHandler(onActivate: () => void, disabled = false) {
  return (e: MouseEvent<HTMLElement>) => {
    if (disabled || (e.target as HTMLElement).closest('button') !== null) return;
    onActivate();
  };
}

/**
 * The one "tapping this row opens its record" idiom — the site map's
 * area-drawer stock rows and the Scheduler Stock tab's
 * outcome box all wear it, and each used to spell out its own role /
 * tabIndex / Enter-or-Space handler.
 *
 * Both handlers refuse activations that belong to a control INSIDE the row:
 * a click that landed on a nested `<button>` is that button's, and a key
 * press is only the row's when the row itself has focus. Rows with no inner
 * controls are unaffected — the guards simply never fire there.
 *
 * `disabled` stands the row down entirely (a row mid-delete) while leaving it
 * rendered and readable.
 */
export function rowActivationProps(
  onActivate: () => void,
  label: string,
  disabled = false,
): IRowActivationProps {
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': label,
    onClick: rowClickHandler(onActivate, disabled),
    onKeyDown: (e) => {
      if (disabled || e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}
